import { Box, Button, Stack, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { formatDateTimeAsUtc } from "@repo/legacy/components/helper/Index";
import type { ProjectPosition, ProjectTimeEntry } from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { TimeEntryRow } from "./TimeEntryRow";
import type { NewTimeEntry } from "./types";
import { createNewEntry } from "./utils";

export type DayDetailPanelProps = {
	date: string;
	entries: ProjectTimeEntry[];
	projectPositions: ProjectPosition[];
	timeTrackingResolution: number;
	projectId: string;
	changedEntries?: (ProjectTimeEntry | NewTimeEntry)[];
	onEntriesChange: (date: string, entries: (ProjectTimeEntry | NewTimeEntry)[]) => void;
	saving?: boolean;
};

export const DayDetailPanel = ({
	date,
	entries,
	projectPositions,
	timeTrackingResolution,
	changedEntries,
	onEntriesChange,
	saving = false,
}: DayDetailPanelProps) => {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	// Use changedEntries (from parent) if available, otherwise use server entries
	const [localEntries, setLocalEntries] = useState<(ProjectTimeEntry | NewTimeEntry)[]>(
		changedEntries ?? entries,
	);

	// Sync: If changedEntries from parent changes (e.g. cancel all), update local state
	useEffect(() => {
		if (changedEntries !== undefined) {
			setLocalEntries(changedEntries);
		} else {
			setLocalEntries(entries);
		}
	}, [changedEntries, entries]);

	// Wrapper to update local state AND notify parent
	const updateEntries = (newEntries: (ProjectTimeEntry | NewTimeEntry)[]) => {
		setLocalEntries(newEntries);
		onEntriesChange(date, newEntries);
	};

	// Handler: Add new entry
	const handleAddEntry = () => {
		if (projectPositions.length === 0) {
			showNotification(t("project-position-required"), "warning");
			return;
		}

		const newEntry = createNewEntry(date, projectPositions[0]!.projectPositionId);
		updateEntries([...localEntries, newEntry]);
	};

	// Handler: Update entry
	const updateLocalEntry = (id: string, updated: (typeof localEntries)[0]) => {
		const updatedEntry = localEntries.find((entry) => entry.id === id);
		if (!updatedEntry) return;
		updatedEntry.start = formatDateTimeAsUtc(updated.start);
		updatedEntry.end = formatDateTimeAsUtc(updated.end);
		updatedEntry.projectPositionId = updated.projectPositionId;
		updatedEntry.description = updated.description;
		updatedEntry.status = updated.status;
		updatedEntry.logs = updated.logs;
		updatedEntry.history = updated.history;
		updateEntries(localEntries.map((entry) => (entry.id === id ? updatedEntry : entry)));
	};

	// Handler: Delete entry
	const deleteLocalEntry = (id: string) => {
		updateEntries(localEntries.filter((entry) => entry.id !== id));
	};

	return (
		<Box sx={{ p: 2, backgroundColor: "background.paper" }}>
			{localEntries.length === 0 ? (
				<Stack alignItems="center" py={3}>
					<Typography variant="body2" color="text.secondary">
						{t("no-time-entries-for-this-day")}
					</Typography>
					<Button
						startIcon={<Add />}
						variant="outlined"
						onClick={handleAddEntry}
						sx={{ mt: 2 }}
						disabled={saving}>
						{t("add-first-entry")}
					</Button>
				</Stack>
			) : (
				<>
					<Stack spacing={2} sx={{ width: "100%" }}>
						{localEntries.map((entry) => (
							<TimeEntryRow
								key={entry.id}
								entry={entry}
								projectPositions={projectPositions}
								timeTrackingResolution={timeTrackingResolution}
								onChange={(updated) => updateLocalEntry(entry.id, updated)}
								onDelete={() => deleteLocalEntry(entry.id)}
								disabled={saving}
							/>
						))}
					</Stack>
				</>
			)}
			{/* Add Entry Button */}
			{localEntries.length > 0 && (
				<Stack direction="row" spacing={2} mt={3} justifyContent="flex-end">
					<Button startIcon={<Add />} variant="outlined" onClick={handleAddEntry} disabled={saving}>
						{t("add-entry")}
					</Button>
				</Stack>
			)}
		</Box>
	);
};
